// An on-page pop-up for a group of images and PDFs (a flat's photos, a floor plan, an EPC graph). Generic so any site
// page can reuse it: pass the items, the one to open at, and onClose. Rendered in a portal over everything.
//
// Keyboard: ← / → move, Esc closes, Tab stays inside (focus starts on Close and returns to whatever opened it).
// Touch: swipe left / right moves. Page scroll is locked while it is open. Styles: .site-lightbox in index.css.
//
// PDFs are drawn page by page with pdf.js (loaded only when a PDF is opened) rather than an <iframe>: Android Chrome
// has no inline PDF viewer and iOS shows only the first page, so an iframe would be blank or partial on most phones.
// The pop-up's Download link is the way to keep the file.
import { useCallback, useEffect, useRef, useState, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';

export interface LightboxItem {
  src: string;
  kind: 'image' | 'pdf';
  alt?: string;
  /** A sentence under the picture. */
  caption?: string | null;
  /** What it shows ("Bedroom 2"), set in small caps above the caption. */
  label?: string | null;
  /** Where "Download" points; defaults to src for PDFs (images get no download link unless given). */
  downloadUrl?: string;
}

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Lightbox({ items, index, onClose, title }: { items: LightboxItem[]; index: number; onClose: () => void; title?: string }) {
  const [at, setAt] = useState(() => Math.min(Math.max(index, 0), items.length - 1));
  const dialog = useRef<HTMLDivElement>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const touch = useRef<{ x: number; y: number } | null>(null);
  const many = items.length > 1;
  const go = useCallback((step: number) => setAt((i) => (i + step + items.length) % items.length), [items.length]);

  // Lock page scroll (keeping the scrollbar's width so nothing jumps), focus Close, and hand focus back on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const { overflow, paddingRight } = document.body.style;
    const bar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (bar > 0) document.body.style.paddingRight = `${bar}px`;
    closeBtn.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
      opener?.focus?.();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (many && e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      else if (many && e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      else if (e.key === 'Tab') trapTab(e, dialog.current);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [many, go, onClose]);

  const item = items[at];
  if (!item) return null;
  const download = item.downloadUrl ?? (item.kind === 'pdf' ? item.src : undefined);

  const onTouchStart = (e: TouchEvent) => { const t = e.touches[0]; touch.current = t ? { x: t.clientX, y: t.clientY } : null; };
  const onTouchEnd = (e: TouchEvent) => {
    const start = touch.current, t = e.changedTouches[0];
    touch.current = null;
    if (!many || !start || !t) return;
    const dx = t.clientX - start.x, dy = t.clientY - start.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx < 0 ? 1 : -1);
  };

  return createPortal(
    <div
      ref={dialog}
      className="site-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={title ?? item.label ?? item.alt ?? 'Image viewer'}
      // A click on the scrim itself (not the picture or a control) closes.
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="site-lightbox-bar">
        <span className="tabular-nums" aria-live="polite">{many ? `${at + 1} / ${items.length}` : title ?? ''}</span>
        <div className="flex items-center gap-1">
          {download && (
            <a href={download} download className="site-lightbox-btn gap-2 px-4 text-[13px] font-medium"><Download className="h-4 w-4" />Download</a>
          )}
          <button ref={closeBtn} type="button" onClick={onClose} className="site-lightbox-btn" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
      </div>

      <figure key={item.src} className="site-lightbox-stage" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        {item.kind === 'pdf' ? (
          <PdfPages key={item.src} url={item.src} name={item.alt ?? item.label ?? 'Document'} />
        ) : (
          <img src={item.src} alt={item.alt ?? item.label ?? ''} className="site-lightbox-img" />
        )}
        {(item.label || item.caption) && (
          <figcaption className="mt-3 flex max-w-[92vw] flex-wrap items-baseline justify-center gap-x-2 text-center text-[13px] text-white/80">
            {item.label && <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-white">{item.label}</span>}
            {item.caption && <span>{item.caption}</span>}
          </figcaption>
        )}
      </figure>

      {many && (
        <>
          <button type="button" onClick={() => go(-1)} className="site-lightbox-btn site-lightbox-nav left-2 sm:left-5" aria-label="Previous photo"><ChevronLeft className="h-6 w-6" /></button>
          <button type="button" onClick={() => go(1)} className="site-lightbox-btn site-lightbox-nav right-2 sm:right-5" aria-label="Next photo"><ChevronRight className="h-6 w-6" /></button>
        </>
      )}
    </div>,
    document.body,
  );
}

/** Keeps Tab and Shift+Tab cycling inside the dialog. */
function trapTab(e: KeyboardEvent, root: HTMLElement | null) {
  if (!root) return;
  const els = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
  const first = els[0], last = els[els.length - 1];
  if (!first || !last) return;
  const active = document.activeElement;
  if (!root.contains(active)) { e.preventDefault(); first.focus(); }
  else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
}

/** A PDF's pages as images in a scrolling white panel (pdf.js, imported on first use so the site does not carry it). */
function PdfPages({ url, name }: { url: string; name: string }) {
  const [pages, setPages] = useState<string[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        const PdfWorker = (await import('pdfjs-dist/build/pdf.worker.min.mjs?worker')).default;
        if (!pdfjs.GlobalWorkerOptions.workerPort) pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();
        const doc = await pdfjs.getDocument({ url }).promise;
        const width = Math.min(1800, Math.max(800, Math.min(window.innerWidth * 0.92, 960) * (window.devicePixelRatio || 1)));
        const out: string[] = [];
        for (let i = 1; i <= doc.numPages && !cancelled; i++) {
          const page = await doc.getPage(i);
          const viewport = page.getViewport({ scale: width / page.getViewport({ scale: 1 }).width });
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(viewport.width); canvas.height = Math.round(viewport.height);
          const ctx = canvas.getContext('2d')!;
          ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
          await page.render({ canvasContext: ctx, viewport }).promise;
          out.push(canvas.toDataURL('image/jpeg', 0.88));
        }
        if (!cancelled) setPages(out);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  return (
    <div className="site-lightbox-pdf" tabIndex={0} aria-label={name} role="document">
      {failed ? <p className="p-8 text-center text-[14px] text-ink-500">This file cannot be shown here. Use Download above to open it.</p>
        : !pages ? <p className="flex h-full items-center justify-center p-8 text-[14px] text-ink-400"><span className="animate-pulse">Opening…</span></p>
          : pages.map((src, i) => <img key={i} src={src} alt={pages.length > 1 ? `${name}, page ${i + 1} of ${pages.length}` : name} className="block w-full border-b border-ink-100 last:border-b-0" />)}
    </div>
  );
}
